import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/["']/g, '').replace(/\/+$/, '');

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDur = (sec) => {
  if (sec === null || sec === undefined) return '—';
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}m ${s}s`;
};

const chip = (bg, fg) => ({ padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: bg, color: fg, textTransform: 'capitalize', marginRight: 6, display: 'inline-block' });
const statusChip = (st) => {
  const map = { completed: ['#d4edda','#155724'], assigned: ['#fff3cd','#856404'], in_progress: ['#cce5ff','#004085'] };
  const [bg, fg] = map[st] || ['#e9ecef','#333'];
  return { padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: bg, color: fg, textTransform: 'capitalize' };
};

export default function TutorStudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [subject, setSubject] = useState('all');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [order, setOrder] = useState('desc');

  const years = useMemo(() => {
    const cur = new Date().getFullYear();
    const list = [];
    for (let y = cur + 1; y >= cur - 4; y--) list.push(y);
    return list;
  }, []);

  useEffect(() => { loadStudent(); }, [studentId]);
  useEffect(() => { if (student) loadExams(); /* refetch on filter change */ }, [subject, year, month, order, student]);

  const headers = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

  const loadStudent = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/tutor/students/${studentId}`, { headers: headers() });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Failed (${res.status})`); }
      const data = await res.json();
      setStudent(data.student);
      setAssignedSubjects(data.assigned_subjects || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadExams = async () => {
    try {
      const qs = new URLSearchParams();
      if (subject && subject !== 'all') qs.set('subject', subject);
      if (year) qs.set('year', year);
      if (year && month) qs.set('month', month);
      qs.set('order', order);
      const res = await fetch(`${API_BASE}/tutor/students/${studentId}/exams?${qs.toString()}`, { headers: headers() });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to load exams'); }
      setExams(await res.json());
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>⏳ Loading student…</div>;
  if (error && !student) return (
    <div style={{ maxWidth: 800, margin: '40px auto', padding: 24 }}>
      <div style={{ padding: 14, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 16 }}>❌ {error}</div>
      <button onClick={() => navigate('/tutor/dashboard')} style={{ padding: '10px 16px', background: '#48A9A6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>← My Students</button>
    </div>
  );
  if (!student) return null;

  const subjectOptions = ['all', ...assignedSubjects];

  return (
    <div style={{ maxWidth: 1100, margin: '30px auto', padding: 24, background: '#f8f9fa', minHeight: '100vh' }}>
      <button onClick={() => navigate('/tutor/dashboard')} style={{ padding: '8px 14px', background: '#48A9A6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginBottom: 16 }}>← My Students</button>

      {/* Profile */}
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 20, marginBottom: 20 }}>
        <h2 style={{ marginTop: 0 }}>{student.first_name} {student.last_name}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 10, fontSize: 14, color: '#333' }}>
          <div><strong>Center ID:</strong> {student.student_code || '—'}</div>
          <div><strong>Username:</strong> {student.username || '—'}</div>
          <div><strong>Grade:</strong> {student.current_grade || '—'}</div>
          <div><strong>School:</strong> {student.school_name || '—'}</div>
          <div><strong>Gender:</strong> {student.gender === 'M' ? 'Male' : student.gender === 'F' ? 'Female' : '—'}</div>
          <div><strong>Age:</strong> {student.age ?? '—'}</div>
          <div><strong>Date of birth:</strong> {fmtDate(student.date_of_birth)}</div>
        </div>
        <div style={{ marginTop: 12 }}>
          <strong>My subjects: </strong>
          {assignedSubjects.map(s => <span key={s} style={chip('#e7f1ff', '#0056b3')}>{s}</span>)}
        </div>
      </div>

      {error && <div style={{ padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 14 }}>❌ {error}</div>}

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Subject</label>
          <select value={subject} onChange={e => setSubject(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }}>
            {subjectOptions.map(s => <option key={s} value={s}>{s === 'all' ? 'All my subjects' : cap(s)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Year</label>
          <select value={year} onChange={e => { setYear(e.target.value); if (!e.target.value) setMonth(''); }} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }}>
            <option value="">All</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Month</label>
          <select value={month} onChange={e => setMonth(e.target.value)} disabled={!year} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', opacity: year ? 1 : 0.5 }}>
            <option value="">All</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Order</label>
          <select value={order} onChange={e => setOrder(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }}>
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </div>
      </div>

      {/* Exam history */}
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
              <th style={{ padding: 12 }}>Date</th>
              <th style={{ padding: 12 }}>Exam</th>
              <th style={{ padding: 12 }}>Subject</th>
              <th style={{ padding: 12 }}>Status</th>
              <th style={{ padding: 12, textAlign: 'right' }}>Score</th>
              <th style={{ padding: 12, textAlign: 'right' }}>Correct</th>
              <th style={{ padding: 12 }}>Duration</th>
              <th style={{ padding: 12 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {exams.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>No exams match these filters.</td></tr>
            ) : exams.map(e => {
              const dateVal = e.completed_at || e.created_at;
              const canReview = e.status === 'completed';
              return (
                <tr key={e.assignment_id} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: 12 }}>{fmtDate(dateVal)}</td>
                  <td style={{ padding: 12 }}>{e.exam_name}</td>
                  <td style={{ padding: 12, textTransform: 'capitalize' }}>{e.subject}</td>
                  <td style={{ padding: 12 }}><span style={statusChip(e.status)}>{e.status}</span></td>
                  <td style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{canReview ? (e.score ?? '—') : '—'}</td>
                  <td style={{ padding: 12, textAlign: 'right' }}>{canReview ? `${e.correct_count ?? 0}/${e.total_questions ?? 0}` : '—'}</td>
                  <td style={{ padding: 12 }}>{canReview ? fmtDur(e.duration_seconds) : '—'}</td>
                  <td style={{ padding: 12 }}>
                    {canReview ? (
                      <button onClick={() => navigate(`/tutor/results/${e.assignment_id}`)} style={{ padding: '6px 12px', background: '#007BFF', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>View Result</button>
                    ) : (
                      <span style={{ color: '#aaa', fontSize: 13 }}>Not started</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}